'use client'

import { Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { ButtonSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import SmallText from '@tebuto/typography/SmallText'
import type React from 'react'
import { type Dispatch, type SetStateAction, useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import LargeText from '../typography/LargeText'
import { waitForExternalLoading } from './confirmation-dialogue-loading'
import { useSelectFirstInput } from './hooks'
import { isInsideOverlayPortal } from './overlay-portal-utils'

export interface IMutationHandlerResponse {
    title: string
    text: string
}

export interface IConfirmationDialogue {
    title: string
    subTitle?: string
    description?: string | React.ReactNode
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    submitButtonText?: string
    cancelButtonText?: string
    onSubmit: (() => void) | (() => Promise<void>)
    onCancel?: () => void
    onClose?: () => void
    submitButtonEnabled?: boolean
    isLoading?: boolean
    children?: React.ReactNode
    width?: 'sm' | 'md' | 'lg'
    dataTestId?: string
    destructiveSubmit?: boolean
}

function ConfirmationDialogue({
    open,
    setOpen,
    onSubmit,
    onCancel = () => {},
    onClose = () => {},
    submitButtonText,
    cancelButtonText,
    submitButtonEnabled = true,
    isLoading,
    title,
    description,
    subTitle,
    children,
    width = 'sm',
    dataTestId,
    destructiveSubmit = false
}: IConfirmationDialogue) {
    const { t } = useComponentTranslation()
    const { modalBodyRef } = useSelectFirstInput()
    const panelRef = useRef<HTMLDivElement>(null)
    const pointerDownInsidePanelRef = useRef(false)
    const isLoadingRef = useRef(isLoading)
    const isPendingRef = useRef(false)
    const titleId = useId()
    const [isSubmitting, setIsSubmitting] = useState(false)

    isLoadingRef.current = isLoading
    const isPending = isSubmitting || !!isLoading
    isPendingRef.current = isPending

    submitButtonText = submitButtonText || t('confirm')
    cancelButtonText = cancelButtonText || t('cancel', { context: 'action' })

    const close = useCallback(() => {
        setOpen(false)
        onClose()
    }, [onClose, setOpen])

    const tryDismiss = useCallback(() => {
        if (isPendingRef.current) {
            return
        }
        close()
    }, [close])

    const handleConfirm = useCallback(async () => {
        if (isPendingRef.current || !submitButtonEnabled) {
            return
        }
        setIsSubmitting(true)
        try {
            await onSubmit()
            if (isLoading !== undefined) {
                await waitForExternalLoading(isLoadingRef)
            }
            close()
        } catch {
            // validation or API error — keep dialog open
        } finally {
            setIsSubmitting(false)
        }
    }, [close, isLoading, onSubmit, submitButtonEnabled])

    const handleCancel = useCallback(() => {
        if (isPendingRef.current) {
            return
        }
        onCancel()
        close()
    }, [close, onCancel])

    useEffect(() => {
        if (!open) {
            return
        }
        pointerDownInsidePanelRef.current = false
        const onPointerDownCapture = (event: PointerEvent): void => {
            const target = event.target
            if (!(target instanceof Node)) {
                return
            }
            const insideWhitePanel = panelRef.current?.contains(target) ?? false
            pointerDownInsidePanelRef.current = insideWhitePanel || isInsideOverlayPortal(target)
        }
        document.addEventListener('pointerdown', onPointerDownCapture, true)
        return (): void => document.removeEventListener('pointerdown', onPointerDownCapture, true)
    }, [open])

    const canSubmit = submitButtonEnabled && !isPending

    const dialogue = (
        <Transition show={open}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="fixed inset-0 z-60 overflow-visible"
                onClick={(event) => {
                    if (event.target instanceof Node && panelRef.current?.contains(event.target)) {
                        return
                    }
                    if (isInsideOverlayPortal(event.target)) {
                        return
                    }
                    if (pointerDownInsidePanelRef.current) {
                        pointerDownInsidePanelRef.current = false
                        return
                    }
                    tryDismiss()
                }}
            >
                <TransitionChild
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0">
                    <div className="flex min-h-screen items-center justify-center text-center sm:items-center">
                        <TransitionChild
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div
                                ref={panelRef}
                                className={cn(
                                    'relative flex flex-col transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:w-full md:max-h-[90vh] max-h-screen',
                                    width === 'sm' ? 'sm:max-w-lg' : '',
                                    width === 'md' ? 'sm:max-w-2xl' : '',
                                    width === 'lg' ? 'sm:max-w-4xl' : ''
                                )}
                                data-testid={dataTestId}
                            >
                                {/* Header */}
                                <div className="bg-primary-25 border-b border-secondary rounded-t-lg shrink-0">
                                    {/* Close Icon */}
                                    <button
                                        type="button"
                                        className={cn(
                                            'absolute right-4 top-4',
                                            isPending ? 'cursor-not-allowed opacity-50' : ''
                                        )}
                                        onClick={tryDismiss}
                                        disabled={isPending}
                                        data-testid={dataTestId ? `${dataTestId}-close` : undefined}
                                    >
                                        <span className="sr-only">{t('modals.closeDialog')}</span>
                                        <XMarkIcon className="h-6 w-6 text-foreground-tertiary hover:cursor-pointer" />
                                    </button>
                                    {/* Title */}
                                    <div className="px-6 py-4">
                                        <div>
                                            {subTitle && <SmallText className="mt-2">{subTitle}</SmallText>}
                                            <LargeText
                                                id={titleId}
                                                className="text-lg font-semibold text-foreground-secondary"
                                            >
                                                {title}
                                            </LargeText>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div
                                    className="px-6 py-6 w-full grow overflow-y-auto min-h-0 text-sm text-muted-foreground"
                                    ref={modalBodyRef}
                                >
                                    {description && <div>{description}</div>}
                                    {children}
                                </div>

                                {/* Buttons */}
                                <div className="bg-primary-25 px-4 py-3 gap-2 sm:px-6 sm:flex sm:flex-row-reverse justify-between border-t border-secondary rounded-b-lg shrink-0">
                                    {
                                        <button
                                            type="button"
                                            className={cn(
                                                'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2  font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto text-sm',
                                                destructiveSubmit
                                                    ? canSubmit
                                                        ? 'bg-destructive hover:bg-destructive/90 hover:cursor-pointer focus:ring-destructive/45'
                                                        : 'bg-destructive/50 hover:cursor-not-allowed'
                                                    : canSubmit
                                                      ? 'bg-primary-brand hover:bg-primary-hover hover:cursor-pointer focus:ring-primary-brand'
                                                      : 'bg-primary-200 hover:cursor-not-allowed'
                                            )}
                                            disabled={!canSubmit}
                                            onClick={() => void handleConfirm()}
                                            data-testid={dataTestId ? `${dataTestId}-confirm` : undefined}
                                        >
                                            {isPending ? <ButtonSpinner tone="on-inverse" /> : submitButtonText}
                                        </button>
                                    }
                                    <button
                                        type="button"
                                        className={cn(
                                            'mt-3 w-full inline-flex justify-center rounded-md border border-secondary shadow-sm px-4 py-2 bg-white font-medium text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-brand sm:mt-0 sm:w-auto text-sm',
                                            isPending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-25'
                                        )}
                                        onClick={handleCancel}
                                        disabled={isPending}
                                        data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
                                    >
                                        {cancelButtonText}
                                    </button>
                                </div>
                            </div>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Transition>
    )

    if (typeof document === 'undefined') {
        return null
    }

    return createPortal(dialogue, document.body)
}

export default ConfirmationDialogue
